import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * The routine section's add-all sheet and phone bar
 * (`sections/ora-lp-routine.liquid`). A card's own Add to cart is Horizon's
 * quick add and needs nothing from here.
 *
 * "Add all" and the phone bar open a `<dialog>` drawn with
 * `ora-partner-stack`'s sheet styles: one row per product, each carrying the
 * variant it will add in `data-id`, its Subscribe & Save plan in `data-plan`
 * (empty where it has none) and both prices in cents. A flavor select's
 * options carry the same values for every sibling flavor; choosing one copies
 * them onto the row, swaps the row's and the offer box's thumbnail, and
 * re-adds every total on the section, so the button adds exactly what the
 * sheet says.
 *
 * The add sends every row to `/cart/add.js` in one request, with
 * `selling_plan` only in subscribe mode, so the routine lands whole or not at
 * all. The cart drawer hears it the way it hears the product form: a
 * `CartLinesUpdateEvent` is dispatched from inside the dialog first, so the
 * drawer waits for the dialog to close before it opens.
 *
 * **A failed cart read after a good add must not leave the drawer dead.** The
 * add response already carries the drawer's sections, so if `/cart.js` fails
 * twice the event resolves with those and a count read out of them -- the
 * same recovery `product-form.js` has (see the cart note in CLAUDE.md).
 */
class OraLpRoutine extends HTMLElement {
  connectedCallback() {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });

    /** @type {HTMLDialogElement | null} */
    this.sheet = this.querySelector('dialog[data-sheet]');
    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector('[data-sheet] [data-add]');
    /** @type {HTMLElement | null} */
    this.status = this.querySelector('[data-status]');
    /** @type {HTMLElement | null} */
    this.bar = this.querySelector('[data-bar]');

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);

    this.watchOffer();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  /** @returns {'subscribe' | 'once'} */
  get mode() {
    return this.dataset.mode === 'once' ? 'once' : 'subscribe';
  }

  /** @returns {HTMLElement[]} */
  get rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-line]')));
  }

  /**
   * The bar shows once the offer box has scrolled up and out of view, and
   * hides again when it comes back -- never while the offer itself is on
   * screen, and never before a reader has reached it.
   */
  watchOffer() {
    const offer = this.querySelector('[data-offer]');
    if (!this.bar || !offer || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry || !this.bar) return;
      this.bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    });
    this.observer.observe(offer);
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.sheet) return;

    if (target.closest('[data-open]')) {
      this.setStatus('');
      this.sheet.showModal();
      return;
    }

    // A click on the dialog element itself, rather than anything inside it,
    // landed on the backdrop.
    if (target.closest('[data-close]') || target === this.sheet) {
      this.sheet.close();
      return;
    }

    if (this.button && target.closest('[data-add]') === this.button) {
      this.add();
    }
  }

  /** @param {Event} event */
  onChange(event) {
    const target = event.target;

    if (target instanceof HTMLInputElement && target.type === 'radio' && this.sheet?.contains(target)) {
      this.dataset.mode = target.value === 'once' ? 'once' : 'subscribe';
      return;
    }

    if (!(target instanceof HTMLSelectElement) || !target.matches('[data-flavor]')) return;
    const row = target.closest('[data-line]');
    const option = target.selectedOptions[0];
    if (!(row instanceof HTMLElement) || !option) return;

    row.dataset.id = option.value;
    row.dataset.plan = option.dataset.plan || '';
    row.dataset.price = option.dataset.price || '0';
    row.dataset.sub = option.dataset.sub || row.dataset.price;

    const price = row.querySelector('[data-price-text]');
    if (price) price.textContent = this.format(Number(row.dataset.price));

    const image = option.dataset.img;
    if (image) {
      const thumbs = [row.querySelector('img[data-thumb]'), this.querySelector(`[data-thumb="${row.dataset.line}"] img`)];
      for (const img of thumbs) {
        if (img instanceof HTMLImageElement) {
          img.removeAttribute('srcset');
          img.src = image;
        }
      }
    }

    this.updateTotals();
  }

  updateTotals() {
    let full = 0;
    let sub = 0;
    for (const row of this.rows) {
      full += Number(row.dataset.price) || 0;
      sub += Number(row.dataset.sub) || Number(row.dataset.price) || 0;
    }
    /** @type {Record<string, string>} */
    const values = {
      full: this.format(full),
      sub: this.format(sub),
      save: String(full > 0 ? Math.floor(((full - sub) * 100) / full) : 0),
    };
    for (const el of this.querySelectorAll('[data-total]')) {
      if (!(el instanceof HTMLElement)) continue;
      // The offer box and the bar carry a bare `data-total` (the one-time
      // sum) and `data-sub-total`; the sheet names which total it wants.
      const key = el.dataset.total || 'full';
      if (key in values) el.textContent = values[key];
    }
    this.querySelectorAll('[data-sub-total]').forEach((el) => (el.textContent = values.sub));
  }

  /** @param {number} cents */
  format(cents) {
    return this.money.format(cents / 100);
  }

  /** @returns {Array<{ id: number, quantity: number, selling_plan?: number }>} */
  items() {
    return this.rows.map((row) => {
      /** @type {{ id: number, quantity: number, selling_plan?: number }} */
      const item = { id: Number(row.dataset.id), quantity: 1 };
      if (this.mode === 'subscribe' && row.dataset.plan) item.selling_plan = Number(row.dataset.plan);
      return item;
    });
  }

  async add() {
    const button = this.button;
    const items = this.items().filter((item) => item.id);
    if (!button || !items.length || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    this.setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await this.post({ items, sections: sectionIds.join(',') });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' }));
        this.setStatus(result.description || result.message || 'Something went wrong. Please try again.');
        const cart = await this.fetchCart().catch(() => null);
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : { totalQuantity: 0 },
          detail: { didError: true, source: 'ora-lp-routine', itemCount: 0 },
        });
        return;
      }

      const cart = await this.fetchCart()
        .catch(() => new Promise((resolve) => setTimeout(resolve, 800)).then(() => this.fetchCart()))
        .catch(() => null);

      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : { totalQuantity: countFrom(result.sections) },
        detail: {
          items: cart ? cart.items : result.items || [],
          source: 'ora-lp-routine',
          sourceId: 'ora-lp-routine',
          itemCount: items.length,
          sections: result.sections,
          didError: false,
        },
      });
      this.sheet?.close();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.setStatus('Something went wrong. Please try again.');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * One retry on a 429, which a burst of app traffic around a cart change
   * can cause on its own (see CLAUDE.md).
   * @param {object} body
   * @returns {Promise<Response>}
   */
  async post(body) {
    const send = () =>
      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    const response = await send();
    if (response.status !== 429) return response;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return send();
  }

  /** @returns {Promise<any>} */
  async fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  /** @param {string} text */
  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }
}

/**
 * The bag count printed in a re-rendered cart section, for when `/cart.js`
 * cannot be read.
 * @param {Record<string, string> | undefined} sections
 * @returns {number}
 */
function countFrom(sections) {
  let count = 0;
  for (const html of Object.values(sections || {})) {
    const match = typeof html === 'string' && html.match(/ref="cartItemCount"[^>]*>\s*(\d+)/);
    if (match) count = Number(match[1]);
  }
  return count;
}

if (!customElements.get('ora-lp-routine')) {
  customElements.define('ora-lp-routine', OraLpRoutine);
}
